<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
          <h3 class="text-lg font-medium text-gray-900">
            模板分类管理
          </h3>
            <p class="text-sm text-gray-500 mt-1">
              创建和管理邮件模板分类
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200 mb-6">
          <nav class="flex space-x-8">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- 创建分类 Tab -->
        <div v-show="activeTab === 'create'" class="space-y-6 mb-6">
          <div>
            <label class="text-sm font-medium text-gray-700 mb-3 block">创建新分类</label>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">分类名称 <span class="text-red-500">*</span></label>
                <input 
                  v-model="newCategoryName"
                  type="text"
                  placeholder="输入分类名称"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  :disabled="isLoading"
                  @keyup.enter="addCategory"
                />
              </div>
              
              <div>
              <button 
                @click="addCategory"
                  :disabled="!newCategoryName.trim() || isLoading"
                  class="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {{ isLoading ? '创建中...' : '创建分类' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分类管理 Tab -->
        <div v-show="activeTab === 'manage'" class="space-y-6">
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">
                邮件模板分类 
                <span class="text-sm text-gray-500">({{ filteredCategories.length }}{{ searchQuery ? ' / ' + categories.length : '' }})</span>
              </label>
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索分类..."
                  class="w-48 h-8 pl-8 pr-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <svg class="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div v-if="isLoadingCategories" class="h-[30vh] flex flex-col items-center justify-center">
              <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>

            <div v-else-if="filteredCategories.length === 0" class="h-[30vh] flex flex-col items-center justify-center text-center text-gray-500">
              <FolderIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p class="text-lg font-medium mb-2">
                {{ searchQuery ? '未找到匹配的分类' : '暂无分类' }}
              </p>
              <p class="text-sm">
                {{ searchQuery ? '尝试使用其他关键词搜索' : '点击"创建分类"标签页创建第一个分类' }}
              </p>
            </div>

            <div v-else class="h-[30vh] overflow-y-auto pr-2 -mr-2" style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;">
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 items-start content-start">
                <div
                  v-for="category in filteredCategories"
                  :key="category.id"
                  class="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors min-h-[40px]"
                >
                  <!-- 分类显示 -->
                  <div class="flex items-center space-x-2 min-w-0 flex-1 overflow-hidden">
                    <FolderIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span class="text-sm font-medium text-gray-900 truncate" :title="category.name">
                      {{ category.name }}
                    </span>
                    <span v-if="category.template_count !== undefined" class="text-xs text-gray-500 flex-shrink-0">
                      {{ category.template_count }}
                    </span>
                  </div>

                  <!-- 操作按钮 -->
                  <div class="flex items-center space-x-1 ml-2 flex-shrink-0">
              <button 
                      @click="handleEditCategory(category)"
                      class="text-blue-600 hover:text-blue-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center"
                      title="编辑分类"
              >
                      ✏️
              </button>
              <button 
                      @click="handleDeleteCategory(category)"
                      :disabled="category.template_count > 0"
                      class="text-red-600 hover:text-red-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center disabled:text-gray-400 disabled:cursor-not-allowed"
                      :title="category.template_count > 0 ? '该分类下还有模板，无法删除' : '删除分类'"
                    >
                      🗑️
              </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部操作按钮 -->
        <div class="flex justify-end pt-6 border-t border-gray-200">
          <button
            @click="closeModal"
            type="button"
            class="inline-flex items-center px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
            :disabled="isLoading"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 编辑分类模态框 -->
  <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50">
    <div class="bg-white rounded-lg shadow-xl w-[90vw] max-w-lg mx-auto">
      <div class="p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900">编辑分类</h3>
          <button
            @click="handleCloseEditModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>
        
        <!-- 编辑表单 -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">分类名称</label>
            <input
              v-model="editingName"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="输入分类名称"
              :disabled="isLoading"
              @keyup.enter="handleUpdateCategory"
            >
          </div>
        </div>
        
        <!-- 操作按钮 -->
        <div class="flex justify-end space-x-3 mt-6">
          <button
            @click="handleCloseEditModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            @click="handleUpdateCategory"
            :disabled="!editingName.trim() || isLoading"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ isLoading ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 删除确认模态框 -->
  <div v-if="deleteConfirm" class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50">
    <div class="bg-white rounded-lg shadow-xl w-[90vw] max-w-md mx-auto">
      <div class="p-6">
        <!-- 标题 -->
        <div class="flex items-center mb-4">
          <ExclamationTriangleIcon class="h-6 w-6 text-red-600 mr-2" />
          <h3 class="text-lg font-semibold text-gray-900">确认删除</h3>
        </div>
        
        <!-- 内容 -->
        <p class="text-sm text-gray-700 mb-6">
          确定要删除分类"<span class="font-medium">{{ deleteConfirm.name }}</span>"吗？此操作无法撤销。
        </p>
        
        <!-- 操作按钮 -->
        <div class="flex justify-end space-x-3">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            @click="deleteCategory"
            :disabled="isLoading"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {{ isLoading ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useToast } from 'vue-toastification'
import { 
  XMarkIcon, 
  FolderIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  categories: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'add-category', 'update-category', 'delete-category', 'refresh'])

// Composables
const toast = useToast()

// State
const newCategoryName = ref('')
const isLoading = ref(false)
const isLoadingCategories = ref(false)
const editingCategory = ref(null)
const editingName = ref('')
const deleteConfirm = ref(null)
const searchQuery = ref('')
const activeTab = ref('create')
const showEditModal = ref(false)

// Tabs
const tabs = ref([
  { id: 'create', name: '创建分类' },
  { id: 'manage', name: '分类管理' }
])

// Computed
const categories = computed(() => props.categories || [])

// 筛选分类
const filteredCategories = computed(() => {
  if (!searchQuery.value) {
    return categories.value
  }
  return categories.value.filter(category => 
    category.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

// Watch for modal show/hide
watch(() => props.show, (show) => {
  if (show) {
    resetState()
    emit('refresh') // 刷新分类列表
  }
})

watch(() => props.loading, (loading) => {
  isLoadingCategories.value = loading
})

// Methods
const resetState = () => {
  newCategoryName.value = ''
  isLoading.value = false
  editingCategory.value = null
  editingName.value = ''
  deleteConfirm.value = null
  searchQuery.value = ''
  activeTab.value = 'create'
  showEditModal.value = false
}

const closeModal = () => {
  if (!isLoading.value) {
    emit('close')
  }
}

const addCategory = async () => {
  const name = newCategoryName.value.trim()
  if (!name) {
    toast.error('请输入分类名称')
    return
  }

  // 检查重复
  if (categories.value.some(cat => cat.name.toLowerCase() === name.toLowerCase())) {
    toast.error('分类名称已存在')
    return
  }

  isLoading.value = true
  
  try {
    emit('add-category', { name })
    newCategoryName.value = ''
    
    // 切换到管理页面
    setTimeout(() => {
      activeTab.value = 'manage'
      isLoading.value = false
    }, 500)
  } catch (error) {
    isLoading.value = false
    toast.error('添加分类失败')
  }
}

const handleEditCategory = (category) => {
  editingCategory.value = category
  editingName.value = category.name
  showEditModal.value = true
}

const handleCloseEditModal = () => {
  editingCategory.value = null
  editingName.value = ''
  showEditModal.value = false
}

const handleUpdateCategory = async () => {
  const name = editingName.value.trim()
  if (!name) {
    toast.error('请输入分类名称')
    return
  }

  // 检查重复（排除当前编辑的分类）
  if (categories.value.some(cat => cat.id !== editingCategory.value.id && cat.name.toLowerCase() === name.toLowerCase())) {
    toast.error('分类名称已存在')
    return
  }

  isLoading.value = true
  
  try {
    emit('update-category', { id: editingCategory.value.id, name })
    
    // 模拟操作完成
    setTimeout(() => {
      isLoading.value = false
      handleCloseEditModal()
    }, 500)
  } catch (error) {
    isLoading.value = false
    toast.error('更新分类失败')
  }
}

const handleDeleteCategory = (category) => {
  if (category.template_count > 0) {
    toast.error('该分类下还有模板，无法删除')
    return
  }
  deleteConfirm.value = category
}

const cancelDelete = () => {
  deleteConfirm.value = null
}

const deleteCategory = async () => {
  if (!deleteConfirm.value) return

  isLoading.value = true
  
  try {
    emit('delete-category', deleteConfirm.value.id)
    
    // 模拟操作完成
    setTimeout(() => {
      isLoading.value = false
      deleteConfirm.value = null
    }, 500)
  } catch (error) {
    isLoading.value = false
    toast.error('删除分类失败')
  }
}
</script>

<style scoped>
/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
